import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  contentSelectors,
  contentOperations,
} from '../models/content/index.js';
import type { Content } from '../models/content/types';
import ContentService from '../services/content';
import { usePatronSelector } from './use-patron-selector';
import { AxiosResponse } from 'axios';

type MentionFormat = 'raw' | 'with_metadata';

interface UseContentFetcherProps {
  contentId: number;
  feedItemId?: string;
  isApproval?: boolean;
  isUgcPreview?: boolean;
  mentionFormat?: MentionFormat;
}

function useContentFetcher({
  contentId,
  feedItemId,
  isApproval,
  isUgcPreview,
  mentionFormat,
}: UseContentFetcherProps) {
  const dispatch = useDispatch();

  const [fetching, setFetching] = useState(true);
  const [content, setContent] = useState<Content>();
  const [error, setError] = useState<unknown>();

  const contentItem = usePatronSelector((state) =>
    contentSelectors.getContentById(state, { contentId })
  );

  const addContentToStore = (c: Content) =>
    dispatch(contentOperations.addContent(c));

  useEffect(() => {
    // FE-34414: always re-fetch for the UGC author preview so the rendered post
    // view reflects the latest publication_state rather than the cached draft.
    // FE-38038: also always re-fetch when a mention_format is requested. A cached
    // Redux contentItem may have been stored by an earlier fetch that requested a
    // different (or no) mention_format — e.g. a feed card load — so reusing it here
    // can hand back a title/summary mention-text.tsx can't linkify, leaving mentions
    // unlinked on the very page (Content View) that explicitly asks for with_metadata.
    if (contentItem && !isUgcPreview && !mentionFormat) {
      setContent(contentItem);
      setFetching(false);
    } else {
      fetchContent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentId, feedItemId, isApproval, isUgcPreview, mentionFormat]);

  const fetchContent = () => {
    setFetching(true);

    const createFetchPromise = (): Promise<AxiosResponse> => {
      // FE-38038: every branch must forward mention_format. Without it pony falls back to
      // its :human_readable default and returns plain `@Name`, which mention-text.tsx
      // cannot linkify (its regex matches only the full @{ref:user::id:N::name:X} form) —
      // so the mention renders as unlinked text. All four ContentService methods accept a
      // params object; previously only the plain .fetch() branch was given one, which
      // silently dropped mentions on the feed-click, approval and UGC-preview paths.
      const params = { mention_format: mentionFormat };

      if (feedItemId) {
        return new ContentService().fetchByFeedItemId(feedItemId, params);
      }
      if (isApproval) {
        return new ContentService(contentId).fetchApproval(params);
      }
      if (isUgcPreview) {
        return new ContentService(contentId).fetchUgcPreview(params);
      }
      return new ContentService(contentId).fetch(params);
    };

    createFetchPromise()
      .then(handleFetchContentSuccess)
      .catch(handleFetchContentError);
  };

  const handleFetchContentSuccess = (res: AxiosResponse) => {
    const contentItem = res.data.feed
      ? res.data.feed[0].attributes
      : res.data.attributes;

    if (res.data.meta?.approval_state) {
      contentItem.approval_state = res.data.meta.approval_state;
    }

    addContentToStore(contentItem);

    setTimeout(() => {
      setContent(contentItem);
      setFetching(false);
    }, 500);
  };

  const handleFetchContentError = (err: unknown) => {
    console.error(err);

    setError(err);
    setFetching(false);
  };

  return {
    fetching,
    content,
    error,
  };
}

export default useContentFetcher;
